export type VoiceNoteRecoveryAction = "record_again" | "retry_send" | "resume_conversation";
export type VoiceNoteFailureStage =
  | "start"
  | "review"
  | "cancel"
  | "recording"
  | "upload"
  | "commit"
  | "retry_prepare"
  | "microphone_restore"
  | "connection";

export class VoiceNoteRejectedError extends Error {}

const reasons = new Set([
  "empty_audio",
  "invalid_audio",
  "audio_not_received",
  "audio_too_large",
  "audio_too_long",
  "agent_not_ready",
  "voice_note_not_ready",
  "voice_note_interrupted",
  "missing_voice_note_id",
  "recording_failed",
  "recording_not_supported",
  "invalid_response",
  "unauthorized",
  "busy",
  "not_recording",
  "voice_note_id_mismatch",
  "session_ended",
  "start_failed",
  "commit_failed",
  "permission_denied",
  "retry_expired",
  "connection_changed",
  "recording_permission_denied",
  "voice_note_start_timeout",
  "voice_note_capture_unavailable",
  "capture_route_changed",
  "capture_track_unavailable",
  "capture_sdk_unavailable",
  "capture_not_active",
]);
const microphoneReasons: Record<string, string> = {
  NotAllowedError: "microphone_permission_denied",
  NotFoundError: "microphone_not_found",
  NotReadableError: "microphone_unavailable",
  OverconstrainedError: "microphone_constraints_failed",
};

export function describeVoiceNoteFailure(
  cause: unknown,
  stage: VoiceNoteFailureStage,
  hasDraft = false,
  surface: "web" | "native" = "web"
) {
  const error = cause as { message?: unknown; name?: unknown; code?: unknown } | null;
  const rpcCode =
    typeof error?.code === "number" &&
    Number.isInteger(error.code) &&
    error.code >= 1000 &&
    error.code <= 1999
      ? error.code
      : undefined;
  const reason =
    typeof error?.message === "string" && reasons.has(error.message)
      ? error.message
      : typeof error?.code === "string" && reasons.has(error.code)
        ? error.code
        : typeof error?.name === "string" &&
            Object.prototype.hasOwnProperty.call(microphoneReasons, error.name)
          ? microphoneReasons[error.name]
          : rpcCode
            ? `rpc_${rpcCode}`
            : `${stage}_failed`;
  let action: VoiceNoteRecoveryAction = "record_again";
  let message = "We couldn't record your voice note. Check your microphone and record again.";

  if (stage === "microphone_restore") {
    action = "resume_conversation";
    message =
      "Your voice note was sent, but your microphone couldn't restart. Resume conversation to try again.";
  } else if (reason === "empty_audio") {
    message = "We couldn't hear you in this recording. Check your microphone and record again.";
  } else if (
    ["invalid_audio", "audio_too_large", "audio_too_long", "recording_failed"].includes(reason)
  ) {
    message = "We couldn't use this recording. Please record your voice note again.";
  } else if (
    hasDraft &&
    (stage === "upload" ||
      stage === "retry_prepare" ||
      (cause instanceof VoiceNoteRejectedError && reason === "audio_not_received"))
  ) {
    action = "retry_send";
    message =
      "Your voice note wasn't sent. It's saved here for up to 30 seconds. Retry sending without recording again.";
  } else if (stage === "commit") {
    action = "resume_conversation";
    message =
      "We couldn't confirm whether your voice note was sent. Check for your tutor's reply before recording again.";
    if (
      cause instanceof VoiceNoteRejectedError &&
      ["not_recording", "voice_note_id_mismatch", "session_ended", "unauthorized"].includes(reason)
    ) {
      action = "record_again";
      message =
        "Your tutor couldn't accept this voice note. Reconnect if needed, then record again.";
    }
  } else if (reason === "retry_expired") {
    message = "The saved recording expired. Please record your voice note again.";
  } else if (stage === "cancel" || stage === "connection") {
    action = "resume_conversation";
    message =
      stage === "connection"
        ? "The connection changed and the recording was discarded. Reconnect before recording again."
        : "Your microphone couldn't restart. Resume conversation to try again.";
  } else if (
    ["microphone_permission_denied", "permission_denied", "recording_permission_denied"].includes(
      reason
    )
  ) {
    message =
      surface === "native"
        ? "Allow microphone access in your phone settings, then record your voice note again."
        : "Allow microphone access in your browser, then record your voice note again.";
  } else if (reason === "recording_not_supported") {
    action = "resume_conversation";
    message = "This browser can't record voice notes. You can still type your answer.";
  } else if (reason === "voice_note_start_timeout") {
    action = "resume_conversation";
    message = "Microphone setup is taking too long. Leave and rejoin the lesson to try again.";
  } else if (
    [
      "capture_route_changed",
      "capture_track_unavailable",
      "capture_sdk_unavailable",
      "voice_note_capture_unavailable",
      "capture_not_active",
    ].includes(reason)
  ) {
    action = "resume_conversation";
    message =
      "We couldn't access your microphone. Leave and rejoin the lesson before recording again.";
  } else if (["agent_not_ready", "busy", "session_ended", "start_failed"].includes(reason)) {
    message =
      "Your tutor isn't ready to record yet. Wait a moment or reconnect, then record again.";
  } else if (reason === "microphone_not_found") {
    message = "We couldn't find a microphone. Connect one, then record your voice note again.";
  } else if (reason === "microphone_unavailable" || reason === "microphone_constraints_failed") {
    message =
      "We couldn't access your microphone. Check your microphone settings and record again.";
  }
  return { reason, stage, rpc_code: rpcCode, action, message };
}
